/**
 * TextViewer Component
 *
 * Displays plain text content with proper formatting.
 * Preserves whitespace and line breaks.
 */

import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Tooltip, Alert } from '@mui/material';
import { ContentCopy, TextFields } from '@mui/icons-material';

interface TextViewerProps {
  content: string;
  filename?: string;
}

const TextViewer: React.FC<TextViewerProps> = ({ content, filename }) => {
  const [copied, setCopied] = useState(false);

  // Handle copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!content || content.trim() === '') {
    return (
      <Alert severity="info">
        No text content available to display
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <TextFields color="primary" />
          {filename && (
            <Typography variant="subtitle1" fontWeight="bold">
              {filename}
            </Typography>
          )}
        </Box>
        <Tooltip title={copied ? 'Copied!' : 'Copy content'}>
          <Button
            size="small"
            startIcon={<ContentCopy />}
            onClick={handleCopy}
            variant={copied ? 'contained' : 'outlined'}
            color={copied ? 'success' : 'primary'}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Tooltip>
      </Box>

      {/* Text Display */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          overflow: 'auto',
          maxHeight: '600px',
          bgcolor: 'grey.50'
        }}
      >
        <Typography
          variant="body2"
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            margin: 0
          }}
        >
          {content}
        </Typography>
      </Paper>

      {/* Footer Info */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        {content.split('\n').length} lines • {content.length} characters
      </Typography>
    </Box>
  );
};

export default TextViewer;
